import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTransactions, useUI } from '../hooks';
import Text from '../components/Text/Text';
import LabeledValue from '../components/LabeledValue';
import CreateDisputeForm from '../components/CreateDisputeForm/CreateDisputeForm';
import { TransactionProgressIndicator, shouldShowProgressIndicator } from '../components/TransactionProgressIndicator';
import { getDisputeByTransactionId } from '../services/disputeService';
import './PaymentSummaryScreen.css';

const PaymentSummaryScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getTransaction, currentTransaction, isLoading, error } = useTransactions();
  const { showToast } = useUI();
  
  const [showDisputeForm, setShowDisputeForm] = useState<boolean>(false);
  const [disputeId, setDisputeId] = useState<string | null>(null);
  
  // Use a ref to track if transaction has been fetched
  const hasLoadedTransaction = useRef<{[key: string]: boolean}>({});
  
  // Fetch transaction on component mount or when id changes
  // We intentionally omit getTransaction and showToast from dependencies to prevent infinite loops
  // The ref ensures we only fetch once for each id regardless of re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchTransaction = async () => {
      if (!id) return;
      
      // Only fetch if we haven't already loaded this transaction
      if (!hasLoadedTransaction.current[id]) {
        try {
          await getTransaction(id);
          // Mark as loaded
          hasLoadedTransaction.current[id] = true;
        } catch (error) {
          showToast('error', 'Failed to load transaction details');
        }
      }
    };
    
    fetchTransaction();
  }, [id]); // Only re-run when id changes
  
  // Check if transaction has a dispute
  useEffect(() => {
    const checkDispute = async () => {
      if (!id) return;
      
      try {
        const disputeData = await getDisputeByTransactionId(id) as any;
        console.log('Dispute data:', disputeData);
        if (disputeData && disputeData.disputes && disputeData.disputes.length > 0) {
          console.log('setDisputeId:', disputeData.disputes[0].id);
          setDisputeId(disputeData.disputes[0].id);
        }
      } catch (error) {
        // Ignore errors, just means no dispute exists
      }
    };
    
    checkDispute();
  }, [id]);
  
  // Handle view cart click
  const handleViewCartClick = () => {
    showToast('info', 'Cart view is not implemented in this demo');
  };
  
  // Handle pay click
  const handlePayClick = () => {
    if (!currentTransaction) return;
    
    showToast('success', `Payment of $${currentTransaction.totalAmount?.toFixed(2) || '0.00'} processed successfully!`);
    navigate('/home');
  };
  
  // Handle open dispute
  const handleOpenDispute = () => {
    setShowDisputeForm(true);
  };
  
  // Handle dispute creation success
  const handleDisputeCreated = (newDisputeId: string) => {
    setDisputeId(newDisputeId);
    showToast('success', 'Dispute created successfully');
    navigate(`/disputes/${newDisputeId}`);
  };
  
  // Navigate to dispute details
  const navigateToDispute = () => {
    console.log('Navigating to dispute:', disputeId);
    if (disputeId) {
      navigate(`/disputes/${disputeId}`);
    }
  };
  
  // Render loading state
  if (isLoading) {
    return (
      <div className="payment-summary-container">
        <div className="loading-state">
          <Text>Loading payment details...</Text>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error || !currentTransaction) {
    return (
      <div className="payment-summary-container">
        <div className="error-state">
          <Text className="text-error">Failed to load payment details</Text>
          <button 
            className="retry-button"
            onClick={() => id && getTransaction(id)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  // Determine if the transaction can be disputed
  const canBeDisputed = currentTransaction &&
    (currentTransaction.status === 'in_escrow' || currentTransaction.status === 'pending');
  
  // Get seller info (in a real app, we would fetch this from the API)
  console.log('Current Transaction:', currentTransaction);
  const sellerId = currentTransaction.sellerPublicId
  
  return (
    
    <div className="payment-summary-container">
      <div className="payment-summary-header">
        <div className="security-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" stroke="#8a2be2" fill="none">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>

      {currentTransaction.status === 'disputed' && (
        <div className="info-panel error escrow-card-section">
          <div className="info-header">
            <Text>This transaction is currently disputed.</Text>
          </div>
          <div>
            <button className="submit-button" onClick={navigateToDispute}>
              View Dispute
            </button>
          </div>
        </div>
      )}
      
      <div className="escrow-card-section">
        <Text className="text-h1">Payment Summary</Text>
        
        <div className="seller-info">
          <LabeledValue label="Seller Id" value={sellerId} />
        </div>
        
        <div className="transaction-info">
          <div className="transaction-id">
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="3" y1="9" x2="21" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="9" y1="21" x2="9" y2="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>
              <LabeledValue label="Transaction Id" value={currentTransaction.id} multiline={true} copyable={true}/>
            </span>
          </div>
          <div className="new-line" />
          <div className="column-align-data-container">
              <LabeledValue
                label="Date"
                value={new Date(currentTransaction.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'})}
                columnAligned={true}
              />
              <LabeledValue
                label="Original Amount"
                value={`$${currentTransaction.originalAmount?.toFixed(2)}`}
                columnAligned={true}
              />
              <LabeledValue
                label="Platform Fee"
                value={`$${currentTransaction.platformFee?.toFixed(2)}`}
                columnAligned={true}
              />
              <LabeledValue
                label="Total Amount"
                value={`$${currentTransaction.totalAmount?.toFixed(2)}`}
                className="text-body text-bold"
                columnAligned={true}
              />
          </div>
        </div>
      </div>
      
      {/* Transaction Progress Indicator */}
      {shouldShowProgressIndicator(currentTransaction) && (
        <div className="escrow-card-section">
          <TransactionProgressIndicator
            transaction={currentTransaction}
            size="medium"
            showLabels={true}
            showIcons={true}
          />
        </div>
      )}
      
      <div className="escrow-card-section">
        <Text className="text-h2">Description</Text>
        <div className="product-item">
          <div className="product-details">
            <Text className="text-body">{currentTransaction.itemDescription}</Text>
          </div>
        </div>
      </div>
      
      <div className="payment-method escrow-card-section">
        <Text className="text-h2">Selected Payment Method</Text>
        {!currentTransaction.paymentMethod ? (
          <div className="payment-method-error">
            <Text className="text-error">Payment method information unavailable</Text>
          </div>
        ) : (
          <div className="payment-method-item">
            <div className="payment-icon">
              {currentTransaction.paymentMethod.paymentType === 'visa' && (
                <svg viewBox="0 0 576 512" width="24" height="24">
                  <path d="M470.1 231.3s7.6 37.2 9.3 45H446c3.3-8.9 16-43.5 16-43.5-.2.3 3.3-9.1 5.3-14.9l2.8 13.4zM576 80v352c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V80c0-26.5 21.5-48 48-48h480c26.5 0 48 21.5 48 48zM152.5 331.2L215.7 176h-42.5l-39.3 106-4.3-21.5-14-71.4c-2.3-9.9-9.4-12.7-18.2-13.1H32.7l-.7 3.1c15.8 4 29.9 9.8 42.2 17.1l35.8 135h42.5zm94.4.2L272.1 176h-40.2l-25.1 155.4h40.1zm139.9-50.8c.2-17.7-10.6-31.2-33.7-42.3-14.1-7.1-22.7-11.9-22.7-19.2.2-6.6 7.3-13.4 23.1-13.4 13.1-.3 22.7 2.8 29.9 5.9l3.6 1.7 5.5-33.6c-7.9-3.1-20.5-6.6-36-6.6-39.7 0-67.6 21.2-67.8 51.4-.3 22.3 20 34.7 35.2 42.2 15.5 7.6 20.8 12.6 20.8 19.3-.2 10.4-12.6 15.2-24.1 15.2-16 0-24.6-2.5-37.7-8.3l-5.3-2.5-5.6 34.9c9.4 4.3 26.8 8.1 44.8 8.3 42.2.1 69.7-20.8 70-53zM528 331.4L495.6 176h-31.1c-9.6 0-16.9 2.8-21 12.9l-59.7 142.5H426s6.9-19.2 8.4-23.3H486c1.2 5.5 4.8 23.3 4.8 23.3H528z">
                  </path>
                </svg>
              )}
              {currentTransaction.paymentMethod.paymentType === 'mastercard' && (
                <svg viewBox="0 0 504 504" width="24" height="24">
                  <circle cx="252" cy="252" r="252" fill="#FF5F00"/>
                  <circle cx="168" cy="252" r="168" fill="#EB001B"/>
                  <circle cx="336" cy="252" r="168" fill="#F79E1B"/>
                  <path d="M252 420c-92.6 0-168-75.4-168-168s75.4-168 168-168 168 75.4 168 168-75.4 168-168 168zm0-336c-92.6 0-168 75.4-168 168s75.4 168 168 168 168-75.4 168-168-75.4-168-168-168z" fill="#FF5F00"/>
                </svg>
              )}
              {currentTransaction.paymentMethod.paymentType === 'applepay' && (
                <svg viewBox="0 0 640 512" width="24" height="24">
                  <path d="M116.9 158.5c-7.5 8.9-19.5 15.9-31.5 14.9-1.5-12 4.4-24.8 11.3-32.6 7.5-9.1 20.6-15.6 31.3-16.1 1.2 12.4-3.7 24.7-11.1 33.8m10.9 17.2c-17.4-1-32.3 9.9-40.5 9.9-8.4 0-21-9.4-34.8-9.1-17.9.3-34.5 10.4-43.6 26.5-18.8 32.3-4.9 80 13.3 106.3 8.9 13 19.5 27.3 33.5 26.8 13.3-.5 18.5-8.6 34.5-8.6 16.1 0 20.8 8.6 34.8 8.4 14.5-.3 23.6-13 32.5-26 10.1-14.8 14.3-29.1 14.5-29.9-.3-.3-28-10.9-28.3-42.9-.3-26.8 21.9-39.5 22.9-40.3-12.5-18.6-32-20.6-38.8-21.1m100.4-36.2v194.9h30.3v-66.6h41.9c38.3 0 65.1-26.3 65.1-64.3s-26.4-64-64.1-64h-73.2zm30.3 25.5h34.9c26.3 0 41.3 14 41.3 38.6s-15 38.8-41.4 38.8h-34.8V165zm162.2 170.9c19 0 36.6-9.6 44.6-24.9h.6v23.4h28v-97c0-28.1-22.5-46.3-57.1-46.3-32.1 0-55.9 18.4-56.8 43.6h27.3c2.3-12 13.4-19.9 28.6-19.9 18.5 0 28.9 8.6 28.9 24.5v10.8l-37.8 2.3c-35.1 2.1-54.1 16.5-54.1 41.5.1 25.2 19.7 42 47.8 42zm8.2-23.1c-16.1 0-26.4-7.8-26.4-19.6 0-12.3 9.9-19.4 28.8-20.5l33.6-2.1v11c0 18.2-15.5 31.2-36 31.2zm102.5 74.6c29.5 0 43.4-11.3 55.5-45.4L640 193h-30.8l-35.6 115.1h-.6L537.4 193h-31.6L557 334.9l-2.8 8.6c-4.6 14.6-12.1 20.3-25.5 20.3-2.4 0-7-.3-8.9-.5v23.4c1.8.4 9.3.7 11.6.7z">
                  </path>
                </svg>
              )}
              {currentTransaction.paymentMethod.paymentType === 'googlepay' && (
                <div className="payment-icon googlepay-icon">G</div>
              )}
            </div>
            <Text className="text-body">
              {currentTransaction.paymentMethod.lastFour
                ? `Payment method ending ${currentTransaction.paymentMethod.lastFour}`
                : 'Payment method details unavailable'}
            </Text>
          </div>
        )}
      </div>

      {/* Dispute Section */}
      {currentTransaction.hasPreviousDisputes && currentTransaction.status !== 'disputed' && (
        <div className="info-panel escrow-card-section">
          <div className="info-text">
            <Text>This transaction was previously disputed.</Text>
            <p>
              <a href='#' className="notification-link" onClick={navigateToDispute}>
                View Dispute History
              </a>
            </p>
          </div>
        </div>
      )}
      
      {!currentTransaction.hasPreviousDisputes && canBeDisputed && (
        <div className="info-panel warning escrow-card-section">
          <div className="info-text">
            <p>If you have an issue with this transaction, you can open a dispute.</p>
          </div>
          <button className="btn btn-warning" onClick={handleOpenDispute}>
            Open Dispute
          </button>
        </div>
      )}
      
      {/* Dispute Form Modal */}
      {showDisputeForm && (
        <CreateDisputeForm
          transactionId={currentTransaction.id}
          onClose={() => setShowDisputeForm(false)}
          onSuccess={handleDisputeCreated}
        />
      )}
    </div>
  );
};

export default PaymentSummaryScreen;